import React from 'react';
import { motion } from 'framer-motion';
import { Moon, Check, Star, ShieldAlert, Download, Clock, Battery, Zap, Brain, Coffee, AlertCircle } from 'lucide-react';
import { SectionWrapper } from '../components/SectionWrapper';
import { CTAButton } from '../components/CTAButton';
import { DayCard } from '../components/DayCard';
import { FAQItem } from '../components/FAQItem';
export function SalesPage() {
  const scrollToCheckout = () => {
    const checkoutSection = document.getElementById('pricing');
    if (checkoutSection) {
      checkoutSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen w-full bg-gradient-to-b from-night-900 via-night-800 to-night-900 text-starlight-200 font-sans selection:bg-amber-400/30" data-id="element-23">
      <div className="max-w-md mx-auto px-5 py-8 pb-16 relative overflow-hidden" data-id="element-24">
        {/* Background Ambient Glows */}
        <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0" data-id="element-25">
          <div className="absolute top-[-10%] left-[-20%] w-[500px] h-[500px] bg-lavender-400/5 rounded-full blur-[100px]" data-id="element-26" />
          <div className="absolute bottom-[-10%] right-[-20%] w-[400px] h-[400px] bg-amber-400/5 rounded-full blur-[80px]" data-id="element-27" />
        </div>

        {/* Content Container */}
        <div className="relative z-10 space-y-12" data-id="element-28">
          {/* SECTION 1: HERO */}
          <header className="text-center space-y-4 pt-4" data-id="element-29">
            <motion.div initial={{
            y: -20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} className="flex justify-center mb-2" data-id="element-30">
              <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-31">
                <Moon className="w-12 h-12 text-amber-400 fill-amber-400/20" data-id="element-32" />
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.92
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="flex justify-center px-4" data-id="element-33">
              <img src="https://cdn.magicpatterns.com/uploads/eu4SVaEdhg6cbgrGDUkfdE/mokup_reguila_el_sueno.png" alt="Regula el sueño en 7 días — portada del libro" className="w-full max-w-[280px] rounded-xl shadow-2xl shadow-night-900/80" data-id="element-34" />
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8
          }} className="text-2xl md:text-3xl font-serif font-bold text-white leading-tight" data-id="element-35">
              Regula tu sueño en 7 días
            </motion.h1>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.3,
            duration: 0.8
          }} className="text-base text-starlight-200/90 leading-relaxed" data-id="element-36">
              Rutina + respiración + hábitos en 7 días para volver a dormir
              profundo.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="pt-2" data-id="element-37">
              <CTAButton onClick={scrollToCheckout} data-id="element-38">Quiero mi plan</CTAButton>
              <p className="text-xs text-starlight-200/50 mt-3 max-w-xs mx-auto" data-id="element-39">
                Acceso inmediato al PDF tras el pago (Shopify). Guía educativa
                (no sustituye atención médica).
              </p>
            </motion.div>
          </header>

          {/* SECTION 2: PAIN POINTS */}
          <SectionWrapper data-id="element-40">
            <div className="bg-night-800/50 rounded-2xl p-6 border border-night-700 backdrop-blur-sm" data-id="element-41">
              <h2 className="text-xl font-serif font-bold text-white mb-4 flex items-center gap-3" data-id="element-42">
                <AlertCircle className="text-lavender-400" data-id="element-43" />
                ¿Te pasa esto?
              </h2>
              <ul className="space-y-3" data-id="element-44">
                {['Te acuestas cansado/a, pero tu mente sigue encendida.', 'Te duermes… y te despiertas en mitad de la noche o demasiado pronto.', 'Duermes "horas", pero te levantas sin sensación de descanso.', 'Vas tirando con café, pero tu energía no se recupera.', 'Notas que el mal sueño te afecta al humor, al enfoque y a la paciencia.'].map((point, i) => <li key={i} className="flex gap-3 text-starlight-200/90" data-id="element-45">
                    <span className="text-amber-500 mt-1" data-id="element-46">•</span>
                    <span data-id="element-47">{point}</span>
                  </li>)}
              </ul>
              <div className="mt-4 pt-4 border-t border-night-700" data-id="element-48">
                <p className="text-lavender-400 font-medium italic" data-id="element-49">
                  Si te identificas, no necesitas "más tips". Necesitas un plan
                  corto y claro que tu cuerpo pueda repetir.
                </p>
              </div>
            </div>
          </SectionWrapper>

          {/* SECTION 3: THE PROBLEM */}
          <SectionWrapper data-id="element-50">
            <h2 className="text-xl font-serif font-bold text-white mb-4" data-id="element-51">
              El problema de los consejos sueltos
            </h2>
            <p className="mb-4 text-starlight-200/80" data-id="element-52">
              La mayoría intenta arreglar el sueño con parches:
            </p>

            <div className="space-y-3 mb-5" data-id="element-53">
              <div className="flex gap-4 items-start" data-id="element-54">
                <div className="bg-night-700 p-2 rounded-lg text-amber-500" data-id="element-55">
                  <Clock size={20} data-id="element-56" />
                </div>
                <div data-id="element-57">
                  <h3 className="font-bold text-white" data-id="element-58">
                    "Hoy sí me acuesto temprano"
                  </h3>
                  <p className="text-sm text-starlight-200/60" data-id="element-59">
                    (pero mañana cambia todo).
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-60">
                <div className="bg-night-700 p-2 rounded-lg text-amber-500" data-id="element-61">
                  <Coffee size={20} data-id="element-62" />
                </div>
                <div data-id="element-63">
                  <h3 className="font-bold text-white" data-id="element-64">
                    Tés, audios, suplementos
                  </h3>
                  <p className="text-sm text-starlight-200/60" data-id="element-65">
                    (sin sistema, no se sostiene).
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-66">
                <div className="bg-night-700 p-2 rounded-lg text-amber-500" data-id="element-67">
                  <Zap size={20} data-id="element-68" />
                </div>
                <div data-id="element-69">
                  <h3 className="font-bold text-white" data-id="element-70">
                    Dejar pantallas "a ratos"
                  </h3>
                  <p className="text-sm text-starlight-200/60" data-id="element-71">
                    (inconsistencia = tu cuerpo no aprende).
                  </p>
                </div>
              </div>
            </div>

            <p className="text-base font-serif text-white text-center border-l-4 border-amber-400 pl-4 py-2 bg-night-800/30 rounded-r-lg" data-id="element-72">
              Dormir mejor no depende de suerte. Depende de señales
              consistentes.
            </p>
          </SectionWrapper>

          {/* SECTION 4: THE CORE IDEA */}
          <SectionWrapper className="bg-gradient-to-br from-lavender-400/10 to-transparent p-6 rounded-2xl border border-lavender-400/20" data-id="element-73">
            <h2 className="text-xl font-serif font-bold text-white mb-3" data-id="element-74">
              La idea que lo cambia todo
            </h2>
            <p className="mb-4 text-starlight-200/90" data-id="element-75">
              Dormir mejor no se improvisa por la noche.{' '}
              <span className="text-amber-400 font-bold" data-id="element-76">
                Se entrena durante el día.
              </span>
            </p>

            <p className="mb-3 text-sm text-starlight-200/70 uppercase tracking-wider font-bold" data-id="element-77">
              Este plan trabaja 3 palancas:
            </p>

            <div className="space-y-3" data-id="element-78">
              {[{
              title: 'Horario fijo',
              desc: 'tu cuerpo anticipa el descanso.'
            }, {
              title: 'Luz + ambiente',
              desc: 'apoyas tu melatonina natural.'
            }, {
              title: 'Rutina anti-estrés',
              desc: 'bajas la activación mental.'
            }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-night-900/50 p-3 rounded-xl border border-night-700" data-id="element-79">
                  <div className="bg-teal-300/20 text-teal-300 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0" data-id="element-80">
                    {i + 1}
                  </div>
                  <p className="text-sm" data-id="element-81">
                    <strong className="text-white" data-id="element-82">{item.title}</strong> →{' '}
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </SectionWrapper>

          {/* SECTION 5: WHAT IS IT? */}
          <SectionWrapper data-id="element-83">
            <h2 className="text-2xl font-serif font-bold text-white mb-4" data-id="element-84">
              ¿Qué es "Regula tu sueño en 7 días"?
            </h2>
            <p className="mb-4 text-base" data-id="element-85">
              Un <strong className="text-amber-400" data-id="element-86">PDF descargable</strong> con
              un plan paso a paso para mejorar tu descanso con cambios pequeños
              pero estratégicos.
            </p>
            <p className="mb-5 text-starlight-200/80" data-id="element-87">
              Está diseñado para que no tengas que pensar "¿y ahora qué hago?":
              sigues el día que toca y listo.
            </p>

            <div className="bg-night-800 p-5 rounded-2xl border border-night-700" data-id="element-88">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2" data-id="element-89">
                <Star className="text-amber-400 w-5 h-5 fill-amber-400" data-id="element-90" />
                Incluye:
              </h3>
              <ul className="space-y-3" data-id="element-91">
                {['Plan de 7 días, con una acción principal por día', 'Técnicas simples de respiración y relajación', 'Recomendaciones para el dormitorio', 'Hábitos de apoyo (alimentación, ejercicio, luz)', 'Evaluación final para ajustar tu rutina'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm text-starlight-200/90" data-id="element-92">
                    <Check className="w-5 h-5 text-teal-300 shrink-0" data-id="element-93" />
                    <span data-id="element-94">{item}</span>
                  </li>)}
              </ul>
            </div>
          </SectionWrapper>

          {/* SECTION 6: DAY BY DAY PLAN */}
          <SectionWrapper data-id="element-95">
            <h2 className="text-xl font-serif font-bold text-white mb-5 text-center" data-id="element-96">
              Tu plan día a día
            </h2>

            <div className="space-y-3" data-id="element-97">
              <DayCard day={1} index={0} title="Horario fijo" description="Acuéstate y levántate a la misma hora (entrenas tu reloj biológico)." data-id="element-98" />
              <DayCard day={2} index={1} title="Alimentación + vitaminas + timing" description="Evitas sabotajes nocturnos (comidas pesadas, cafeína y alcohol cerca de la noche)." data-id="element-99" />
              <DayCard day={3} index={2} title="Ambiente ideal" description="Dormitorio como santuario (16–19°C, oscuridad, silencio/ruido blanco) + pantallas fuera 60 min antes." data-id="element-100" />
              <DayCard day={4} index={3} title="Rutina relajante" description="15–30 minutos para bajar revoluciones (leer, baño tibio, música suave, diario)." data-id="element-101" />
              <DayCard day={5} index={4} title="Siestas + ejercicio + sol" description="Más sueño profundo por la noche (actividad física con buen horario y luz por la mañana)." data-id="element-102" />
              <DayCard day={6} index={5} title="Estrés y ansiedad" description="Herramientas para cortar rumiación (diario de preocupaciones, mindfulness, respiración 4-7-8)." data-id="element-103" />
              <DayCard day={7} index={6} title="Evaluación" description="Revisas mejoras y ajustas tu rutina (lo haces sostenible)." data-id="element-104" />
            </div>
          </SectionWrapper>

          {/* SECTION 7: RESULTS */}
          <SectionWrapper data-id="element-105">
            <div className="text-center mb-5" data-id="element-106">
              <h2 className="text-xl font-serif font-bold text-white mb-1" data-id="element-107">
                Lo que deberías notar
              </h2>
              <p className="text-sm text-starlight-200/60" data-id="element-108">
                con el tiempo y la constancia
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-109">
              {[{
              icon: Battery,
              text: 'Más energía estable'
            }, {
              icon: Brain,
              text: 'Mejor concentración'
            }, {
              icon: Moon,
              text: 'Menos despertares'
            }, {
              icon: CheckCircle2,
              text: 'Menos fricción al dormir'
            }].map((item, i) => <div key={i} className="bg-night-800 p-3 rounded-xl border border-night-700 flex flex-col items-center text-center gap-1.5" data-id="element-110">
                  <item.icon className="w-7 h-7 text-lavender-400" data-id="element-111" />
                  <span className="text-sm font-medium text-starlight-100" data-id="element-112">
                    {item.text}
                  </span>
                </div>)}
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl" data-id="element-113">
              <p className="text-xs text-amber-200/80 leading-relaxed" data-id="element-114">
                <strong data-id="element-115">Nota:</strong> Los resultados varían según la persona y
                el contexto. Si tras 2–3 semanas persisten dificultades
                significativas, lo responsable es consultar con un especialista
                del sueño.
              </p>
            </div>
          </SectionWrapper>

          {/* SECTION 8: PRICING & ACCESS */}
          <SectionWrapper data-id="element-116">
            <div id="pricing" className="bg-gradient-to-b from-night-800 to-night-900 border border-amber-500/30 rounded-2xl p-6 text-center relative overflow-hidden shadow-2xl shadow-amber-900/20" data-id="element-117">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-50" data-id="element-118" />

              <h2 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-119">
                Regula tu sueño en 7 días
              </h2>
              <div className="inline-block bg-night-900 px-3 py-1 rounded-full border border-night-700 text-xs font-bold text-starlight-200/60 mb-4" data-id="element-120">
                FORMATO PDF DIGITAL
              </div>

              <div className="mb-5" data-id="element-121">
                <span className="text-5xl font-serif font-bold text-amber-400" data-id="element-122">
                  $6,39
                </span>
                <span className="text-lg text-starlight-200/60 ml-2" data-id="element-123">USD</span>
              </div>

              <p className="text-sm text-starlight-200/80 mb-5" data-id="element-124">
                Acceso inmediato tras la compra en Shopify.
              </p>

              <CTAButton href="https://www.cuidarte360.com/cart/55940144431430:1" className="mb-4" data-id="element-125">
                Quiero mi plan
              </CTAButton>

              <div className="flex items-center justify-center gap-2 text-xs text-starlight-200/40" data-id="element-126">
                <ShieldAlert className="w-3 h-3" data-id="element-127" />
                <span data-id="element-128">Pago 100% seguro</span>
              </div>
            </div>
          </SectionWrapper>

          {/* SECTION 9: POST PURCHASE */}
          <SectionWrapper data-id="element-129">
            <h2 className="text-lg font-serif font-bold text-white mb-4" data-id="element-130">
              ¿Qué pasa después del pago?
            </h2>
            <div className="space-y-4 relative" data-id="element-131">
              <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-night-700" data-id="element-132" />

              {[{
              title: 'Finalizas la compra',
              desc: 'Pago seguro en Shopify.'
            }, {
              title: 'Accedes a la descarga',
              desc: 'En pantalla y/o por email.'
            }, {
              title: 'Empiezas el Día 1',
              desc: 'Hoy mismo.'
            }].map((step, i) => <div key={i} className="flex gap-4 relative z-10" data-id="element-133">
                  <div className="w-8 h-8 rounded-full bg-night-800 border-2 border-lavender-400 flex items-center justify-center text-lavender-400 font-bold text-sm shrink-0" data-id="element-134">
                    {i + 1}
                  </div>
                  <div data-id="element-135">
                    <h3 className="font-bold text-white" data-id="element-136">{step.title}</h3>
                    <p className="text-sm text-starlight-200/60" data-id="element-137">{step.desc}</p>
                  </div>
                </div>)}
            </div>
          </SectionWrapper>

          {/* SECTION 10: FAQ */}
          <SectionWrapper data-id="element-138">
            <h2 className="text-xl font-serif font-bold text-white mb-4" data-id="element-139">
              FAQ (rápido)
            </h2>
            <div className="space-y-2" data-id="element-140">
              <FAQItem question="¿Y si tengo horarios complicados?" answer="No necesitas perfección: necesitas un ancla (especialmente la hora de despertar) y una rutina breve. El plan te guía para construir consistencia realista." data-id="element-141" />
              <FAQItem question="¿Y si me despierto a media noche?" answer="Por eso el plan incluye ambiente (oscuridad/temperatura/ruido), timing de comidas/cafeína/alcohol y herramientas para bajar activación (respiración/relajación)." data-id="element-142" />
              <FAQItem question="¿Esto es médico?" answer="No. Es educación y hábitos. Si el problema es persistente o intenso, consulta a un profesional." data-id="element-143" />
            </div>
          </SectionWrapper>

          {/* SECTION 11: FINAL CTA */}
          <SectionWrapper data-id="element-144">
            <div className="text-center pt-8 pb-4" data-id="element-145">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-6 leading-tight" data-id="element-146">
                Empieza hoy.
                <br data-id="element-147" />
                <span className="text-lavender-400" data-id="element-148">
                  En 7 días tendrás un patrón.
                </span>
              </h2>

              <CTAButton onClick={scrollToCheckout} price="$6,39" data-id="element-149">
                Quiero mi plan
              </CTAButton>

              <div className="mt-4 flex flex-col items-center gap-1.5 text-sm text-starlight-200/50" data-id="element-150">
                <p data-id="element-151">Acceso inmediato al PDF.</p>
                <p data-id="element-152">Pago seguro con Shopify.</p>
              </div>
            </div>
          </SectionWrapper>

          {/* Footer */}
          <footer className="text-center text-xs text-starlight-200/20 py-6 border-t border-night-800" data-id="element-153">
            <p data-id="element-154">
              © {new Date().getFullYear()} Regula tu sueño. Todos los derechos
              reservados.
            </p>
          </footer>
        </div>
      </div>
    </div>;
}
function CheckCircle2({
  className
}: {
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-155">
      <circle cx="12" cy="12" r="10" data-id="element-156" />
      <path d="m9 12 2 2 4-4" data-id="element-157" />
    </svg>;
}